import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';

const ContactPage = () => {
    const contactInfo = [
        { 
            icon: Phone, 
            label: 'Phone / WhatsApp', 
            value: '07440 747050',
            href: 'tel:07440747050'
        },
        { 
            icon: Mail, 
            label: 'Email', 
            value: 'info@hopeaccountancy.co.uk',
            href: 'mailto:info@hopeaccountancy.co.uk'
        },
        { 
            icon: MapPin, 
            label: 'Address', 
            value: '713A Green Lane, Dagenham, RM8 1UU, UK',
            href: 'https://maps.google.com/?q=713A+Green+Lane+Dagenham+RM8+1UU'
        },
        { 
            icon: Clock, 
            label: 'Opening Hours', 
            value: 'Mon–Fri: 08:00 AM – 07:00 PM',
            href: null
        },
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        const form = e.target;
        const name = form.name.value;
        const email = form.email.value;
        const phone = form.phone.value;
        const subject = form.subject.value;
        const message = form.message.value;
        
        const mailtoLink = `mailto:info@hopeaccountancy.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
            `Name: ${name}\nEmail: ${email}\nPhone: ${phone}\n\nMessage:\n${message}`
        )}`;
        
        window.location.href = mailtoLink;
    };

    return (
        <main data-testid="contact-page">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-hero-dark overflow-hidden">
                <div className="absolute inset-0 noise-texture" />
                <div className="absolute top-0 right-0 w-96 h-96 bg-hope-soft/20 rounded-full blur-3xl" />
                
                <div className="container-hope relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-3xl"
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/90 font-medium text-sm mb-6">
                            Contact Us
                        </span>
                        <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                            Get In Touch
                        </h1>
                        <p className="text-xl text-white/70 leading-relaxed">
                            Ready to take control of your finances? We'd love to hear from you.
                        </p>
                    </motion.div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F1F8F4"/>
                    </svg>
                </div>
            </section>

            {/* Contact Section */}
            <section className="section-padding bg-hope-light">
                <div className="container-hope">
                    <div className="grid lg:grid-cols-2 gap-16">
                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <GlassCard className="bg-white">
                                <h2 className="font-heading font-bold text-2xl text-hope-forest mb-6">
                                    Send Us a Message
                                </h2>
                                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Your Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                required
                                                data-testid="contact-name-input"
                                                className="form-input"
                                                placeholder="John Smith"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                data-testid="contact-email-input"
                                                className="form-input"
                                                placeholder="john@example.com"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                data-testid="contact-phone-input"
                                                className="form-input"
                                                placeholder="07XXX XXXXXX"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Subject *
                                            </label>
                                            <input
                                                type="text"
                                                name="subject"
                                                required
                                                data-testid="contact-subject-input"
                                                className="form-input"
                                                placeholder="General Enquiry"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Your Message *
                                        </label>
                                        <textarea
                                            name="message"
                                            required
                                            rows={5}
                                            data-testid="contact-message-input"
                                            className="form-textarea"
                                            placeholder="Tell us how we can help you..."
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        data-testid="contact-submit-btn"
                                        className="btn-premium btn-primary w-full flex items-center justify-center gap-3"
                                    >
                                        <Send className="w-5 h-5" />
                                        Send Message
                                    </button>
                                </form>
                            </GlassCard>
                        </motion.div>

                        {/* Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div>
                                <h2 className="font-heading font-bold text-2xl text-hope-forest mb-4">
                                    Contact Information
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    Get in touch with us through any of the following channels. We're here to help!
                                </p>
                            </div>

                            <div className="space-y-6">
                                {contactInfo.map((item, index) => (
                                    <motion.div
                                        key={item.label}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <GlassCard className="bg-white flex items-start gap-5 py-5">
                                            <div className="w-14 h-14 rounded-2xl bg-hope-emerald flex items-center justify-center flex-shrink-0">
                                                <item.icon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">{item.label}</p>
                                                {item.href ? (
                                                    <a 
                                                        href={item.href}
                                                        target={item.href.startsWith('http') ? '_blank' : undefined}
                                                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                        className="text-hope-forest font-medium hover:text-hope-soft transition-colors"
                                                    >
                                                        {item.value}
                                                    </a>
                                                ) : (
                                                    <p className="text-hope-forest font-medium">{item.value}</p>
                                                )}
                                            </div>
                                        </GlassCard>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Map Embed */}
                            {/* Map Embed */}
<GlassCard className="bg-white p-0 overflow-hidden">
    <iframe
        src="https://www.google.com/maps?q=713+Green+Ln,+Dagenham+RM8+1UU,+UK&output=embed"
        width="100%"
        height="250"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="Hope Accountancy Location"
        className="rounded-3xl"
    />
</GlassCard>
                        </motion.div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default ContactPage;
